import { CoinIcon } from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import { useInView, useReducedMotion } from "motion/react";
import React from "react";

// Manifest key token/USDv (PNG preferred).
import usdv from "../../../../../../../.claude/design-system/assets/tokens/USDv.png";

// Illustration coordinates, not reserve amounts. Unequal sectors deliberately
// carry no percentages or balances: these are examples of backing assets.
const RADIUS = 78;
const TILT = 0.62;
const DEPTH = 6;
const CYCLE_MS = 10000;
const RECORD_WIDTH = 124;
const RECORD_HEIGHT = 60;
const RECORD_HALF_WIDTH = RECORD_WIDTH / 2;
const RECORD_HALF_HEIGHT = RECORD_HEIGHT / 2;

function ReserveCallout(): React.ReactElement {
    return (
        <div className="relative flex h-full w-full items-center gap-2 bg-surface-ground px-2 py-1">
            <span
                aria-hidden="true"
                className="pointer-events-none absolute left-0.5 right-0 top-0 h-0.5 bg-line-tint-neutral"
            />
            <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-0 top-0.5 w-0.5 bg-line-tint-neutral"
            />
            <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-0 right-0.5 h-0.5 bg-line-tint-neutral"
            />
            <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0.5 right-0 top-0 w-0.5 bg-line-tint-neutral"
            />
            <span className="relative z-10 shrink-0 text-line-tint-neutral">
                <CoinIcon className="size-6" />
            </span>
            <span className="relative z-10 flex min-w-0 flex-1 flex-col gap-1">
                <span className="h-0.5 w-full bg-line-tint-neutral" />
                <span className="h-0.5 w-3/4 bg-line-tint-neutral" />
                <span className="h-0.5 w-1/2 bg-line-tint-neutral" />
            </span>
        </div>
    );
}

interface Point {
    x: number;
    y: number;
}

interface ReserveSlice {
    start: number;
    sweep: number;
    offset: Point;
    record: Point;
    source: Point;
}

const SLICES: readonly ReserveSlice[] = [
    {
        start: 140,
        sweep: 205,
        offset: { x: 0, y: -18 },
        record: { x: 185, y: 28 },
        source: { x: 225, y: 96 }
    },
    {
        start: 345,
        sweep: 55,
        offset: { x: 28, y: 18 },
        record: { x: 490, y: 210 },
        source: { x: 385, y: 190 }
    },
    {
        start: 40,
        sweep: 100,
        offset: { x: 2, y: 34 },
        record: { x: 280, y: 300 },
        source: { x: 280, y: 220 }
    }
];

function pointAt(degrees: number): Point {
    const angle = (degrees * Math.PI) / 180;
    return { x: Math.cos(angle) * RADIUS, y: Math.sin(angle) * RADIUS * TILT };
}

function sectorPath(start: number, sweep: number): string {
    const from = pointAt(start);
    const to = pointAt(start + sweep);
    const largeArc = sweep > 180 ? 1 : 0;
    return `M0 0L${from.x} ${from.y}A${RADIUS} ${RADIUS * TILT} 0 ${largeArc} 1 ${to.x} ${to.y}Z`;
}

function sidePath(start: number, sweep: number): string {
    const from = pointAt(start);
    const to = pointAt(start + sweep);
    const largeArc = sweep > 180 ? 1 : 0;
    return `M${from.x} ${from.y}A${RADIUS} ${RADIUS * TILT} 0 ${largeArc} 1 ${to.x} ${to.y}L${to.x} ${to.y + DEPTH}A${RADIUS} ${RADIUS * TILT} 0 ${largeArc} 0 ${from.x} ${from.y + DEPTH}Z M0 0L${from.x} ${from.y}V${from.y + DEPTH}L0 ${DEPTH}Z M0 0L${to.x} ${to.y}V${to.y + DEPTH}L0 ${DEPTH}Z`;
}

function recordEndpoint(slice: ReserveSlice): Point {
    const isTopRecord = slice.record.y < 100;
    const isBottomRecord = slice.record.y > 250;
    return {
        x:
            isTopRecord || isBottomRecord
                ? slice.record.x
                : slice.record.x +
                  (slice.record.x < 280
                      ? RECORD_HALF_WIDTH
                      : -RECORD_HALF_WIDTH),
        y: isTopRecord
            ? slice.record.y + RECORD_HALF_HEIGHT
            : isBottomRecord
              ? slice.record.y - RECORD_HALF_HEIGHT
              : slice.record.y
    };
}

interface SliceStyle extends React.CSSProperties {
    "--slice-x": string;
    "--slice-y": string;
    "--trace-x": string;
    "--trace-y": string;
}

const RESERVES_CSS = `
    .reserves-illustration { --reserves-cycle: ${CYCLE_MS}ms; }
    .reserves-slice { animation: reserves-separate var(--reserves-cycle) var(--ease-in-out-cubic) infinite; }
    .reserves-trace { opacity: 0; animation: reserves-trace var(--reserves-cycle) var(--ease-in-out-cubic) infinite; }
    .reserves-trace path { stroke-dasharray: 0.01 0.03; }
    .reserves-trace-reveal { stroke-dasharray: 1; stroke-dashoffset: 1; animation: reserves-trace-draw var(--reserves-cycle) var(--ease-in-out-cubic) infinite; }
    .reserves-record { opacity: 0; animation: reserves-record var(--reserves-cycle) var(--ease-out-cubic) infinite; }
    .reserves-entry { opacity: 0; animation: reserves-entry var(--reserves-cycle) var(--ease-in-out-cubic) infinite; }
    .reserves-illustration[data-motion="paused"] [data-reserves-motion] { animation-play-state: paused; }
    @keyframes reserves-separate {
        0%, 14%, 86%, 100% { transform: translate(0, 0); }
        28%, 70% { transform: translate(var(--slice-x), var(--slice-y)); }
    }
    @keyframes reserves-trace {
        0%, 39%, 79%, 100% { opacity: 0; }
        40%, 60%, 72% { opacity: 1; }
    }
    @keyframes reserves-trace-draw {
        0%, 39%, 79%, 100% { stroke-dashoffset: 1; }
        40% { stroke-dashoffset: 1; }
        60%, 72% { stroke-dashoffset: 0; }
    }
    @keyframes reserves-record {
        0%, 28%, 86%, 100% { opacity: 0; }
        38%, 70% { opacity: 1; }
    }
    @keyframes reserves-entry {
        0%, 30% { opacity: 0; transform: translate(0, 0); }
        34%, 40% { opacity: 1; transform: translate(0, 0); }
        60% { opacity: 1; transform: translate(var(--trace-x), var(--trace-y)); }
        64%, 100% { opacity: 0; transform: translate(var(--trace-x), var(--trace-y)); }
    }
    @media (prefers-reduced-motion: reduce) {
        .reserves-illustration [data-reserves-motion] { animation: none; }
        .reserves-slice { transform: translate(var(--slice-x), var(--slice-y)); }
        .reserves-trace { opacity: 0.65; }
        .reserves-trace-reveal { stroke-dashoffset: 0; }
        .reserves-record { opacity: 1; }
    }
`;

/** A conceptual reconciliation cycle. No live holdings or allocation implied. */
export function ReservesEmptyState(): React.ReactElement {
    const id = React.useId();
    const ref = React.useRef<HTMLElement>(null);
    const inView = useInView(ref, { amount: 0.25 });
    const reducedMotion = useReducedMotion();
    const [visible, setVisible] = React.useState(true);

    React.useEffect(() => {
        const update = (): void => setVisible(!document.hidden);
        update();
        document.addEventListener("visibilitychange", update);
        return (): void =>
            document.removeEventListener("visibilitychange", update);
    }, []);

    return (
        <figure
            ref={ref}
            className="reserves-illustration mx-auto flex w-full max-w-xl flex-col items-center gap-4 py-6"
            data-motion={
                inView && visible && !reducedMotion ? "running" : "paused"
            }
            aria-label="Conceptual illustration: a USDv coin separates into three sections linked to generic reserve records, then reunites as the records are reconciled. Not actual reserve allocations."
        >
            <style dangerouslySetInnerHTML={{ __html: RESERVES_CSS }} />
            <svg
                viewBox="0 -24 560 374"
                fill="none"
                className="block w-full"
                aria-hidden="true"
            >
                <defs>
                    {SLICES.map((slice) => (
                        <clipPath
                            key={slice.start}
                            id={`${id}-slice-${slice.start}`}
                        >
                            <path d={sectorPath(slice.start, slice.sweep)} />
                        </clipPath>
                    ))}
                    {SLICES.map((slice) => {
                        const end = recordEndpoint(slice);
                        return (
                            <mask
                                key={`trace-${slice.start}`}
                                id={`${id}-trace-${slice.start}`}
                                maskUnits="userSpaceOnUse"
                                x={0}
                                y={0}
                                width={560}
                                height={350}
                            >
                                <rect width={560} height={350} fill="black" />
                                <path
                                    d={`M${slice.source.x} ${slice.source.y}L${end.x} ${end.y}`}
                                    pathLength={1}
                                    stroke="white"
                                    strokeWidth={8}
                                    strokeLinecap="round"
                                    className="reserves-trace-reveal"
                                    data-reserves-motion
                                />
                            </mask>
                        );
                    })}
                </defs>
                {SLICES.map((slice) => {
                    const end = recordEndpoint(slice);
                    const style: SliceStyle = {
                        "--slice-x": `${slice.offset.x}px`,
                        "--slice-y": `${slice.offset.y}px`,
                        "--trace-x": `${end.x - slice.source.x}px`,
                        "--trace-y": `${end.y - slice.source.y}px`
                    };
                    return (
                        <g key={slice.start} style={style}>
                            <g
                                className="reserves-trace stroke-line-tint-neutral"
                                data-reserves-motion
                            >
                                <path
                                    d={`M${slice.source.x} ${slice.source.y}L${end.x} ${end.y}`}
                                    pathLength={1}
                                    mask={`url(#${id}-trace-${slice.start})`}
                                    data-reserves-motion
                                />
                            </g>
                            <g
                                transform={`translate(${slice.source.x} ${slice.source.y})`}
                            >
                                <rect
                                    x={-2}
                                    y={-2}
                                    width={4}
                                    height={4}
                                    className="reserves-entry fill-accent-base"
                                    data-reserves-motion
                                />
                            </g>
                            <g
                                transform={`translate(${slice.record.x} ${slice.record.y})`}
                            >
                                <foreignObject
                                    x={-RECORD_HALF_WIDTH}
                                    y={-RECORD_HALF_HEIGHT}
                                    width={RECORD_WIDTH}
                                    height={RECORD_HEIGHT}
                                    className="reserves-record"
                                    data-reserves-motion
                                >
                                    <ReserveCallout />
                                </foreignObject>
                            </g>
                        </g>
                    );
                })}
                <g transform="translate(280 150)">
                    {SLICES.map((slice) => (
                        <g
                            key={slice.start}
                            style={
                                {
                                    "--slice-x": `${slice.offset.x}px`,
                                    "--slice-y": `${slice.offset.y}px`
                                } as React.CSSProperties
                            }
                        >
                            <g className="reserves-slice" data-reserves-motion>
                                <path
                                    d={sectorPath(slice.start, slice.sweep)}
                                    transform={`translate(0 ${DEPTH})`}
                                    className="fill-surface-raised stroke-line-base"
                                />
                                <path
                                    d={sidePath(slice.start, slice.sweep)}
                                    className="fill-surface-raised stroke-line-base"
                                />
                                <path
                                    d={sectorPath(slice.start, slice.sweep)}
                                    className="fill-surface-ground"
                                />
                                <image
                                    href={usdv}
                                    x={-RADIUS}
                                    y={-RADIUS * TILT}
                                    width={RADIUS * 2}
                                    height={RADIUS * TILT * 2}
                                    preserveAspectRatio="none"
                                    clipPath={`url(#${id}-slice-${slice.start})`}
                                />
                                <path
                                    d={sectorPath(slice.start, slice.sweep)}
                                    className="stroke-line-base"
                                />
                            </g>
                        </g>
                    ))}
                </g>
            </svg>
            <figcaption className="flex flex-col items-center gap-1 text-center">
                <Text
                    variant="body"
                    render={<h3 />}
                    className="text-foreground-base"
                >
                    Failed to Load Data
                </Text>
                <Text
                    variant="small"
                    render={<p />}
                    className="text-foreground-neutral"
                >
                    Please try again later
                </Text>
            </figcaption>
        </figure>
    );
}
